import { config } from '@/config';
import { consumePkce, generatePkce, stashPkce } from './pkce';
import { clearTokens, loadTokens, saveTokens } from './tokenStorage';

interface TokenResponse { id_token: string; refresh_token?: string; expires_in: number }
const REFRESH_SLOP_MS = 60_000;

function assertConfigured(): void {
  if (!config.cognito.domain || !config.cognito.clientId) throw new Error('admin_auth_not_configured');
}

async function tokenRequest(body: URLSearchParams): Promise<TokenResponse> {
  const response = await fetch(`https://${config.cognito.domain}/oauth2/token`, {
    method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body,
  });
  if (!response.ok) throw new Error(`cognito_token_failed_${response.status}`);
  return response.json() as Promise<TokenResponse>;
}

export async function signIn(returnTo = window.location.pathname): Promise<void> {
  assertConfigured();
  const pkce = await generatePkce();
  stashPkce(pkce, returnTo);
  const params = new URLSearchParams({
    client_id: config.cognito.clientId, response_type: 'code', redirect_uri: config.cognito.redirectUri,
    scope: 'openid email profile', code_challenge: pkce.challenge, code_challenge_method: 'S256', state: pkce.state,
  });
  window.location.assign(`https://${config.cognito.domain}/oauth2/authorize?${params}`);
}

export async function completeSignIn(code: string, state: string): Promise<string> {
  assertConfigured();
  const pkce = consumePkce();
  if (!pkce) throw new Error('missing_pkce_state');
  if (pkce.state !== state) throw new Error('oauth_state_mismatch');
  const tokens = await tokenRequest(new URLSearchParams({
    grant_type: 'authorization_code', client_id: config.cognito.clientId, code,
    redirect_uri: config.cognito.redirectUri, code_verifier: pkce.verifier,
  }));
  saveTokens({ idToken: tokens.id_token, refreshToken: tokens.refresh_token || '', expiresAt: Date.now() + tokens.expires_in * 1_000 });
  return pkce.returnTo;
}

export async function getIdToken(): Promise<string | null> {
  const stored = loadTokens();
  if (!stored) return null;
  if (stored.expiresAt - Date.now() > REFRESH_SLOP_MS) return stored.idToken;
  if (!stored.refreshToken) { clearTokens(); return null; }
  try {
    const fresh = await tokenRequest(new URLSearchParams({
      grant_type: 'refresh_token', client_id: config.cognito.clientId, refresh_token: stored.refreshToken,
    }));
    saveTokens({ idToken: fresh.id_token, refreshToken: fresh.refresh_token || stored.refreshToken, expiresAt: Date.now() + fresh.expires_in * 1_000 });
    return fresh.id_token;
  } catch {
    clearTokens();
    return null;
  }
}

export function signOut(): void {
  clearTokens();
  if (!config.cognito.domain || !config.cognito.clientId) { window.location.assign('/auth/signed-out'); return; }
  const params = new URLSearchParams({ client_id: config.cognito.clientId, logout_uri: `${window.location.origin}/auth/signed-out` });
  window.location.assign(`https://${config.cognito.domain}/logout?${params}`);
}
